import type UserDialogProperties from './UserDialog.properties.ts';
import { type ChangeEvent, useState } from 'react';
import { LanguageCode, Roles, type UserUserIO } from '../../generated/fresh-guard-api';
import { prepareObjectForSmartSelect } from '../../tools/converters/SmartSelectConverter.ts';
import { useUserUpdateOptimistic } from '../../apis/user/useUserUpdateOptimistic.ts';
import { toast } from 'react-toastify';
import { toastOptions } from '../../tools/helpers/ToastHelper.ts';
import { useTranslation } from 'react-i18next';
import { isPasswordLongEnough } from '../../tools/helpers/PasswordPolicy.ts';

export const useUserDialogController = (props: UserDialogProperties) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState<string>(props.user?.username ?? '');
  const [firstname, setFirstname] = useState<string>(props.user?.firstname ?? '');
  const [lastname, setLastname] = useState<string>(props.user?.lastname ?? '');
  const [email, setEmail] = useState<string>(props.user?.email ?? '');
  const [password, setPassword] = useState<string>('');
  const [repeatPassword, setRepeatPassword] = useState<string>('');
  const [languageCode, setLanguageCode] = useState<string>(
    (Object.entries(LanguageCode)
      .find(([, value]) => props.user?.languageCode === value)
      ?.at(1) as LanguageCode) ??
      Object.values(LanguageCode).at(0) ??
      ''
  );
  const [roles, setRoles] = useState<string[]>(
    props.user?.roles?.filter((role): role is string => role !== null) ?? [Roles.RoleUser]
  );

  const { mutate, isPending } = useUserUpdateOptimistic();

  const handleLanguageCodeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setLanguageCode(event.target.value);
  };

  const handleRolesChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setRoles(JSON.parse(event.target.value));
  };

  const prepareLanguageCodeForSmartSelect = () => {
    return prepareObjectForSmartSelect(LanguageCode);
  };

  const prepareRolesForSmartSelect = () => {
    return prepareObjectForSmartSelect(Roles);
  };

  const enableSaveButton = () => {
    if (props.user) {
      return !(
        !username.trim() ||
        !firstname.trim() ||
        !lastname.trim() ||
        !languageCode.trim() ||
        roles.length === 0
      );
    } else {
      return !(
        !username.trim() ||
        !firstname.trim() ||
        !lastname.trim() ||
        !isPasswordLongEnough(password) ||
        password !== repeatPassword ||
        !languageCode.trim() ||
        roles.length === 0
      );
    }
  };

  const handleSave = () => {
    const user: UserUserIO = {
      username: username,
      firstname: firstname,
      lastname: lastname,
      email: email,
      password: password.trim(),
      languageCode: languageCode,
      roles: roles,
      permissions: [], // will be set automatically by role on the backend
    };

    mutate(
      { userUserIO: user },
      {
        onSuccess: (user) => {
          props.handleDialogCloseClick(user);
          toast.success(t('success.userSaved'), toastOptions);
        },
      }
    );
  };

  return {
    handleSave,
    username,
    setUsername,
    firstname,
    setFirstname,
    lastname,
    setLastname,
    languageCode,
    email,
    setEmail,
    password,
    setPassword,
    repeatPassword,
    setRepeatPassword,
    roles,
    handleLanguageCodeChange,
    handleRolesChange,
    prepareLanguageCodeForSmartSelect,
    prepareRolesForSmartSelect,
    enableSaveButton,
    loading: isPending,
  };
};
